import { useState } from "react";
import { Stack, Alert, Text, Paper, Box, Button, Affix } from "@mantine/core";
import "./DynamicFormRenderer.css";
import { IconAlertCircle, IconClipboardText } from "@tabler/icons-react";
import type {
  FormSchemaV2,
  FieldElement,
  LookupFieldType,
  LookupOption,
} from "@livingsyncs/types";
import { isFormSchemaV2 } from "@livingsyncs/types";
import { isFieldVisible } from "./engines/ConditionEngine";
import { SectionRenderer } from "./SectionRenderer";
import { validateForm } from "./engines/ValidationEngine";
import { FormBranding } from "./FormBranding";
import { FormTitleSection } from "./FormTitleSection";
import { LookupDrawer, type LookupDrawerItem } from "./LookupDrawer";
import {
  BORDER_COLOR,
  BORDER_WIDTH,
  SPACING,
  BACKGROUND,
  buildInterpolationContext,
} from "./designTokens";

// Forms are always rendered on a white-paper surface regardless of the app's
// color scheme. Setting these CSS custom properties as inline styles cascades
// to every nested Mantine component (Paper, Text, Input…) and always wins
// over the dark-theme stylesheet — inline style specificity is absolute.
const LIGHT_MODE_VARS: React.CSSProperties = {
  // Explicit `color` (not just the var): Mantine's <Text> sets no color and
  // INHERITS from <body>, whose color was computed at :root from the dark-scheme
  // `--mantine-color-text` (a light grey) — so without this, value text inherits
  // light grey and renders faint on the white sheet. Setting color here makes the
  // whole form subtree inherit the forced-dark text.
  color: "var(--mantine-color-text)",
  "--mantine-color-body": "#ffffff",
  "--mantine-color-surface": "#ffffff",
  "--mantine-color-text": "#212529",
  "--mantine-color-bright": "#000000",
  "--mantine-color-dimmed": "#868e96",
  "--mantine-color-placeholder": "#adb5bd",
  "--mantine-color-default": "rgba(0,0,0,0)",
  "--mantine-color-default-hover": "#f8f9fa",
  "--mantine-color-default-border": "#ced4da",
  "--mantine-color-default-color": "#212529",
} as React.CSSProperties;

export interface DynamicFormRendererProps {
  schema: unknown;
  initialData?: Record<string, unknown>;
  onDataChange?: (data: Record<string, unknown>) => void;
  /** When true the renderer also runs validation and exposes errors inline. */
  showValidationErrors?: boolean;
  /**
   * Field errors returned by the backend (a 400's `details`), rendered inline
   * regardless of showValidationErrors. Server verdicts outrank client ones
   * for the same field (US-081 map-or-toast contract).
   */
  serverErrors?: Record<string, string[]>;
  readOnly?: boolean;
  /**
   * Real, backend-sourced choices for entity-lookup field types, keyed by
   * FieldType (e.g. the resident's active medications for medication_lookup,
   * known allergies for resident_allergy_lookup). A missing key means no
   * context is available on this surface — the field renders a disabled
   * "resolved at fill time" state (US-133 AC-6) rather than free text.
   */
  lookupOptions?: Partial<Record<LookupFieldType, LookupOption[]>>;
  /** Static context values for {{variable}} interpolation in title/subtitle. */
  staticContext?: {
    resident_name?: string;
    facility_name?: string;
    employee_name?: string;
    submission_date?: string;
    form_name?: string;
  };
  /**
   * Lookup Drawer follow-up: real resident/facility reference data (physicians,
   * pharmacies, insurance, emergency contacts, care plans, allergies,
   * medications, facility info) the filler can search and insert into
   * whichever text/textarea field they last clicked into. Omit (or pass an
   * empty array) on surfaces with no such context (e.g. a staff-audience
   * form) — the trigger button only renders when there's something to search.
   */
  lookupDrawerItems?: LookupDrawerItem[];
}

/**
 * Renders a FormSchemaV2 as a live React form.
 *
 * - Manages field values internally; fires onDataChange on every change.
 * - Applies conditional logic (visibleIf / requiredIf) reactively.
 * - Shows inline validation errors when showValidationErrors is true.
 * - Applies FormSettings (page, branding, titleSection) from schema.settings.
 * - If schema is not v2, renders a legacy-template notice instead of crashing.
 */
// Stable empty object — avoids creating a new {} on every render which would
// cause any equality-based sync to loop infinitely.
const EMPTY_INITIAL_DATA: Record<string, unknown> = {};

export function DynamicFormRenderer({
  schema,
  initialData = EMPTY_INITIAL_DATA,
  onDataChange,
  showValidationErrors = false,
  serverErrors,
  readOnly = false,
  lookupOptions,
  staticContext,
  lookupDrawerItems,
}: DynamicFormRendererProps) {
  const [values, setValues] = useState<Record<string, unknown>>(initialData);
  // Lookup Drawer follow-up: which field the filler last clicked into (see
  // FieldRenderer's onFieldFocus), and whether the drawer itself is open.
  // Both declared unconditionally, before the legacy-schema early return
  // below, per the Rules of Hooks.
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [drawerOpened, setDrawerOpened] = useState(false);

  if (!isFormSchemaV2(schema)) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        color="yellow"
        title="Legacy template"
      >
        <Text size="sm">
          This form was built with an older format. Open it in the Form Builder
          and re-save to upgrade to the new layout engine.
        </Text>
      </Alert>
    );
  }

  const typedSchema = schema as FormSchemaV2;

  // Derived, not state: callers flip showValidationErrors on after a failed
  // submit attempt, and errors must appear on that render — not on the next
  // field change. Server-reported field errors (a 400's details) render
  // regardless and outrank the client verdict for the same field.
  const errors: Record<string, string[]> = {
    ...(showValidationErrors ? validateForm(typedSchema, values) : {}),
    ...(serverErrors ?? {}),
  };

  const settings = typedSchema.settings;
  const page = settings?.page;
  const branding = settings?.branding;
  const titleSection = settings?.titleSection;

  const handleChange = (id: string, value: unknown) => {
    const next = { ...values, [id]: value };
    setValues(next);
    onDataChange?.(next);
  };

  // Lookup Drawer follow-up — find the currently-focused field's label (for
  // "Inserting into: <label>" context in the drawer) by walking the schema.
  const activeField: FieldElement | undefined = activeFieldId
    ? typedSchema.sections
        .flatMap((s) => s.elements)
        .find(
          (el): el is FieldElement =>
            el.kind === "field" && el.id === activeFieldId,
        )
    : undefined;

  // Code-review fix ("couldn't insert weight here"): a number-type target
  // field can only ever hold a bare numeric value — never copyText's full
  // sentence, and never an append (there's no "append" for a number). An
  // item with no numericValue simply cannot go into a number field at all
  // (LookupDrawer already greys those out / disables the click for this
  // exact reason, so this branch is a defensive no-op, not the primary gate).
  const activeFieldIsNumber = activeField?.type === "number";

  // Appends rather than replaces (and never overwrites silently) for
  // text-like targets: a filler may want to insert several references into
  // the same narrative field (e.g. "Recent Falls") across multiple drawer
  // clicks.
  const handleInsert = (item: LookupDrawerItem) => {
    if (!activeFieldId || !activeField) return;
    // Deep-review fix: a field can be hidden by a visibleIf condition AFTER
    // it was focused (editing the trigger field) — inserting into it would
    // write into a value the filler can no longer see or correct.
    if (!isFieldVisible(activeField.visibleIf, values)) return;
    if (activeFieldIsNumber) {
      if (item.numericValue === undefined) return;
      // Deep-review fix: element-level min/max (how AI-generated numeric
      // scales carry their bounds — e.g. pain 0–10) is a NumberInput UI
      // constraint that only clamps TYPED input; submit-time validation
      // reads validation.min/max, never element.min/max. Without this
      // guard, inserting "Weight 165" into a 0–10 scale wrote an
      // out-of-range clinical value that submitted silently.
      if (activeField.min != null && item.numericValue < activeField.min)
        return;
      if (activeField.max != null && item.numericValue > activeField.max)
        return;
      handleChange(activeFieldId, item.numericValue);
      return;
    }
    const current = values[activeFieldId];
    const existing = typeof current === "string" ? current : "";
    // Code-review fix: only the trimmed value decides WHETHER to prefix a
    // separator — the raw `existing` (not its trimmed form) is what's kept,
    // so boundary whitespace the user already typed is never discarded.
    const next = existing.trim()
      ? `${existing}\n${item.copyText}`
      : item.copyText;
    handleChange(activeFieldId, next);
  };

  const interpolationContext = buildInterpolationContext(
    values as Record<string, unknown>,
    staticContext,
  );

  // Build page container style from tokens
  const pageStyle: React.CSSProperties = {
    ...(page?.padding ? { padding: SPACING[page.padding] } : {}),
    ...(page?.background
      ? { backgroundColor: BACKGROUND[page.background] }
      : {}),
    ...(page?.borderEnabled
      ? {
          border: `${BORDER_WIDTH[page.borderWidth ?? "sm"]} solid ${BORDER_COLOR[page.borderColor ?? "neutral"]}`,
          borderRadius: 8,
        }
      : {}),
  };

  const content = (
    <Stack gap="md">
      {branding && (
        <FormBranding
          settings={branding}
          interpolationContext={interpolationContext}
        />
      )}

      {titleSection && (
        <FormTitleSection
          settings={titleSection}
          interpolationContext={interpolationContext}
        />
      )}

      {typedSchema.sections.map((section, sectionIndex) => (
        <SectionRenderer
          key={section.id}
          section={section}
          sectionIndex={sectionIndex}
          values={values}
          onChange={handleChange}
          readOnly={readOnly}
          errors={errors}
          lookupOptions={lookupOptions}
          onFieldFocus={readOnly ? undefined : setActiveFieldId}
        />
      ))}
    </Stack>
  );

  // Code-review fix: the trigger used to render for ANY non-empty
  // lookupDrawerItems, even on a schema with no focus-trackable field (e.g.
  // all select/checkbox/lookup fields) or in read-only mode — a dead end
  // where the button always opens a drawer that can never insert anywhere.
  const hasFocusableField =
    !readOnly &&
    typedSchema.sections.some((s) =>
      s.elements.some(
        (el) =>
          el.kind === "field" &&
          // Code-review fix: number now focus-tracks too (numericValue
          // insertion), so it counts toward "this schema has somewhere to insert."
          (["text", "email", "phone", "textarea", "number"] as const).includes(
            el.type as never,
          ),
      ),
    );

  const lookupDrawer = hasFocusableField &&
    lookupDrawerItems &&
    lookupDrawerItems.length > 0 && (
      <>
        <Affix position={{ bottom: 20, right: 20 }}>
          <Button
            leftSection={<IconClipboardText size={16} />}
            onClick={() => setDrawerOpened(true)}
            radius="xl"
          >
            Look up info
          </Button>
        </Affix>
        <LookupDrawer
          opened={drawerOpened}
          onClose={() => setDrawerOpened(false)}
          items={lookupDrawerItems}
          activeFieldLabel={activeField?.label ?? null}
          activeFieldIsNumber={activeFieldIsNumber}
          onInsert={handleInsert}
        />
      </>
    );

  // Always wrap in the light-mode CSS variable scope so section Papers,
  // inputs, and text remain on white paper regardless of the app's color
  // scheme, and present the form on the "sheet" — schema page tokens are
  // inline styles, so they override the sheet's CSS defaults where set.
  if (page) {
    return (
      <Box style={LIGHT_MODE_VARS}>
        <Paper
          className="ls-form-sheet"
          style={pageStyle}
          withBorder={false}
          shadow="none"
        >
          {content}
        </Paper>
        {lookupDrawer}
      </Box>
    );
  }

  return (
    <Box style={LIGHT_MODE_VARS}>
      <Paper className="ls-form-sheet" withBorder={false} shadow="none">
        {content}
      </Paper>
      {lookupDrawer}
    </Box>
  );
}
